import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Button, Modal, ModalBody, ModalFooter, ModalHeader,
    Form, FormGroup, Label, Input, Row, Col, Spinner,
} from 'reactstrap';
import DatePicker from '../utility/datePicker';
import { CURRENCIES, DEFAULT_CURRENCY } from '../../constants/currencies';
import useJobTypes from '../../hooks/useJobTypes';
import useConvertQuoteToJob from '../../hooks/useConvertQuoteToJob';

// Convert-to-job isn't a pure confirm — the operator often needs to stamp a
// Job # (or let the server generate one) and pick dates. Everything here is
// optional; the backend fills gaps (auto "J-####" when JobNumber is blank,
// today when StartDate is blank). Site address is authored on the Quote and
// copied to the Job automatically — no re-entry needed here.
export default function ConvertQuoteModal({ isOpen, onClose, quote }) {
    const navigate = useNavigate();
    const convert = useConvertQuoteToJob();
    const { data: jobTypes = [] } = useJobTypes();

    const [jobNumber, setJobNumber] = useState('');
    // Default the picker to today — matches the server-side fallback so what
    // the user sees is what actually gets saved if they don't touch it.
    const todayLocal = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };
    const [startDate, setStartDate] = useState(todayLocal);
    const [endDate, setEndDate] = useState('');
    // Prefill from the quote's customer when we have it; otherwise fall back
    // to GBP. The server will also prefill from the customer if we send
    // null, so this is purely a UI hint — the dropdown is editable.
    const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
    const [jobType, setJobType] = useState('');

    // Reset on open so a previously-entered-but-cancelled value doesn't leak
    // into the next conversion. Parents only toggle isOpen; the modal never
    // unmounts between opens.
    useEffect(() => {
        if (!isOpen) return;
        setJobNumber('');
        setStartDate(todayLocal());
        setEndDate('');
        setCurrency(quote?.customerCurrency ?? DEFAULT_CURRENCY);
        setJobType('');
    }, [isOpen, quote?.customerCurrency]);

    const handleClose = () => {
        if (convert.isPending) return;
        onClose();
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!quote?.id || convert.isPending) return;
        const body = {
            jobNumber: jobNumber.trim() || null,
            startDate: startDate || null,
            endDate:   endDate   || null,
            // Site address is copied from the quote automatically server-side.
            // Always send currency — the server falls back to the customer's
            // default if null, but the UI always has a chosen value.
            currency,
            jobType: jobType || null,
        };
        convert.mutate({ id: quote.id, body }, {
            onSuccess: ({ jobId }) => {
                // Close before navigating so the modal fade state doesn't
                // carry over to /jobs/:id.
                onClose();
                navigate(`/jobs/${jobId}`);
            },
        });
    };

    return (
        <Modal isOpen={isOpen} toggle={handleClose} centered backdrop="static" size="lg">
            <ModalHeader toggle={handleClose}>Convert quote to job</ModalHeader>
            <Form onSubmit={handleSubmit} noValidate>
                <ModalBody>
                    <p className="text-muted small mb-3">
                        A new job will be created from{' '}
                        <strong>{quote?.quoteNumber ?? 'this quote'}</strong>. The quote will
                        be marked <strong>Converted</strong> and can no longer be edited. The
                        new job's <strong>Quoted Revenue</strong> and{' '}
                        <strong>Estimated Cost</strong> are seeded from this quote's totals.
                    </p>

                    <FormGroup>
                        <Label>Job #</Label>
                        <Input
                            value={jobNumber}
                            onChange={(e) => setJobNumber(e.target.value)}
                            placeholder="Auto-generated if blank..."
                            className="placeholder-italic"
                        />
                    </FormGroup>

                    <Row>
                        <Col md={4}>
                            <FormGroup>
                                <Label>Start Date</Label>
                                <DatePicker value={startDate} handleChange={setStartDate} isClearable />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label>End Date</Label>
                                <DatePicker value={endDate} handleChange={setEndDate} isClearable />
                            </FormGroup>
                        </Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label>Currency</Label>
                                <Input type="select" value={currency}
                                       onChange={(e) => setCurrency(e.target.value)}>
                                    {CURRENCIES.map((c) => (
                                        <option key={c.code} value={c.code}>
                                            {c.code} — {c.symbol} {c.name}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>

                    <FormGroup>
                        <Label>Job Type <span className="text-muted fw-normal">(optional)</span></Label>
                        <Input type="select" value={jobType}
                               onChange={(e) => setJobType(e.target.value)}>
                            <option value="">-- Select type --</option>
                            {jobTypes.map(t => (
                                <option key={t.id} value={t.name}>{t.name}</option>
                            ))}
                        </Input>
                    </FormGroup>

                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" outline onClick={handleClose}
                            disabled={convert.isPending}>
                        Cancel
                    </Button>
                    <Button color="primary" type="submit" disabled={convert.isPending}>
                        {convert.isPending && <Spinner size="sm" className="me-1" />}
                        Convert
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
}
